'use client';

import * as React from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useTheme } from "@/components/theme-provider";

/*
Using this component:

import GalleryCard from '@/components/ui/cardGallery';

<GalleryCard
  title="Card Title"
  subtitle="Card subtitle text"
  backgroundImageUrl="/path/to/image.jpg"
  backgroundImageAlt="Description of image"
  withGradient={true}
  fadeIn={true}
  cardNumberSequence={0}
  className="h-64 cursor-pointer"
  titleProps={{ className: "text-xl" }}
  subtitleProps={{ className: "text-sm opacity-90" }}
  cardLight={true} // Optional: true for black text with neutral background, defaults to gray text with zinc background
  onClick={() => console.log('Card clicked')}
>
  <div className="mt-2">
    Optional children content
  </div>
</GalleryCard>
*/

const GalleryCard = React.forwardRef(({
  title,
  subtitle,
  backgroundImageUrl,
  backgroundImageSizes,
  backgroundImageAlt,
  titleProps = {},
  subtitleProps = {},
  withGradient = true,
  fadeIn = false,
  cardNumberSequence = 0,
  cardLight,
  className,
  children,
  ...props
}, ref) => {
  const { theme } = useTheme();

  const isLightTheme = theme === "light" || cardLight === true;
  const textColor = isLightTheme ? 'text-black/80' : 'text-gray-300';
  const bgColor = withGradient 
    ? (isLightTheme ? 'bg-neutral-200/100' : 'bg-zinc-800/100')
    : (isLightTheme ? 'bg-white' : 'bg-black');
  
  const borderColor = withGradient 
    ? ''
    : (isLightTheme ? ' border border-black/5' : 'border border-violet-500/30 ');

  // Check if card has a link (onClick or href)
  const hasLink = props.onClick || props.href;

  const CardComponent = (
    <Card
      ref={ref}
      className={cn(
        "relative overflow-hidden p-0 transition-shadow hover:shadow-lg hover:shadow-violet-500/30 h-96",
        borderColor,
        className
      )}
      {...props}
    >
      {backgroundImageUrl && (
        <motion.div
          initial={{ scale: 1 }}
          whileHover={hasLink ? { scale: 1.1 } : { scale: 1 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 z-0"
        >
          <img
            src={backgroundImageUrl}
            alt={backgroundImageAlt}
            className="w-full h-full object-top object-contain"
          />
        </motion.div>
      )}
      
      {withGradient && (
        <div 
          className="absolute inset-0 z-10 pointer-events-none"
          style={{
            background: `linear-gradient(0deg, ${isLightTheme ? 'rgb(229 229 229 / 1)' : 'rgb(39 39 42 / 1)'} 25%, ${isLightTheme ? 'rgb(229 229 229 / 0.8)' : 'rgb(39 39 42 / 0.8)'} 35%, transparent 45%)`
          }}
        />
      )}
      
      <CardContent 
        className={cn(
          "z-20 pointer-events-none select-none border-t border-t-black/30 dark:border-t-violet-500/30",
          withGradient 
            ? "relative px-3 sm:px-6 h-full flex flex-col justify-end p-4"
            : `absolute bottom-0 left-0 right-0 ${bgColor} px-3 sm:px-6 py-4`
        )}
      >
        <div className="space-y-0 px-0 pb-4">
          {title && (
            <h3
              className={cn(
                "text-lg font-bold pb-2",
                textColor,
                titleProps.className
              )}
              style={{ textWrap: 'balance', ...titleProps.style }}
              {...titleProps}
            >
              {title}
            </h3>
          )}
          {subtitle && (
            <p
              className={cn(
                "text-sm opacity-80",
                textColor,
                subtitleProps.className
              )}
              style={{ textWrap: 'balance', ...subtitleProps.style }}
              {...subtitleProps}
            >
              {subtitle}
            </p>
          )}
        </div>
        {children}
      </CardContent>
    </Card>
  );

  if (fadeIn) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ 
          duration: 0.6, 
          delay: cardNumberSequence * 0.1,
          ease: [0.25, 0.46, 0.45, 0.94] 
        }}
        viewport={{ once: false, margin: "-50px" }}
      >
        {CardComponent}
      </motion.div>
    );
  }

  return CardComponent;
});

GalleryCard.displayName = "GalleryCard";

const WideGalleryCard = React.forwardRef(({
  image1Url,
  image1HoverUrl,
  image2Url,
  image2HoverUrl,
  textContent,
  image1Width = '33%',
  image2Width = '33%',
  textWidth = '33%',
  order = 'image-image-text', // 'image-image-text', 'image-text-image', 'text-image-image'
  className,
  bgIndex = 0,
  imageStretch = false,
  ...props
}, ref) => {
    const bgOpacity = '/20';
    const borderOpacity = '/30';
    const bgColor = `bg-statusBotGradient-${bgIndex}${bgOpacity}`;
    const gradientDirections = ['tl', 'tr', 'bl', 'br', 'bl', 'br', 'tl', 'tr', 'tl', 'tr'];
    const bgColorFade = `bg-gradient-to-${gradientDirections[bgIndex]} from-statusBotGradient-${bgIndex}${bgOpacity} to-background`;
    const borderColor = `border-statusBotGradient-${bgIndex}${borderOpacity}`;
  
  const renderImage1 = () => (
    <div className="p-4 flex items-center justify-center lg:hidden w-full md:w-1/2" style={{ flex: `0 0 ${image1Width}` }}>
      <div className={`relative overflow-hidden rounded group w-full h-full border ${borderColor}`}>
        <img src={image1Url} alt="Image 1" className={`w-full h-auto object-cover transition-opacity duration-500 scale-[1.01] rounded ${imageStretch ? 'object-contain' : 'object-cover'}`} />
        {image1HoverUrl && (
          <img 
            src={image1HoverUrl} 
            alt="Image 1 Hover" 
            className={`absolute inset-0 w-full h-auto object-cover opacity-0 group-hover:opacity-100 transition-all duration-700 delay-200 transform translate-y-[-40%] group-hover:translate-y-0 scale-[1.01] rounded`} 
          />
        )}
      </div>
    </div>
  );

  const renderImage1Desktop = () => (
    <div className="hidden lg:flex p-4 items-center justify-center" style={{ flex: `0 0 ${image1Width}` }}>
      <div className={`relative overflow-hidden rounded group w-full h-full border ${borderColor}`}>
        <img src={image1Url} alt="Image 1" className={`w-full h-auto object-cover transition-opacity duration-500 scale-[1.01] rounded ${imageStretch ? 'object-contain' : 'object-cover'}`} />
        {image1HoverUrl && (
          <img 
            src={image1HoverUrl} 
            alt="Image 1 Hover" 
            className="absolute inset-0 w-full h-auto object-cover opacity-0 group-hover:opacity-100 transition-all duration-700 delay-200 transform translate-y-[-40%] group-hover:translate-y-0 scale-[1.01] rounded" 
          />
        )}
      </div>
    </div>
  );

  const renderImage2 = () => {
    if (!image2Url) return null;
    return (
      <div className="p-4 flex items-center justify-center lg:hidden w-full md:w-1/2" style={{ flex: `0 0 ${image2Width}` }}>
        <div className="relative overflow-hidden rounded group w-full h-full">
          <img src={image2Url} alt="Image 2" className={`w-full h-auto object-cover transition-opacity duration-500 rounded ${imageStretch ? 'object-contain' : 'object-cover'}`} />
          {image2HoverUrl && (
            <img 
              src={image2HoverUrl} 
              alt="Image 2 Hover" 
              className="absolute inset-0 w-full h-auto object-cover opacity-0 group-hover:opacity-100 transition-all duration-700 delay-200 transform translate-y-[-40%] group-hover:translate-y-0 rounded" 
            />
          )}
        </div>
      </div>
    );
  };

  const renderImage2Desktop = () => {
    if (!image2Url) return null;
    return (
      <div className="hidden lg:flex p-4 items-center justify-center" style={{ flex: `0 0 ${image2Width}` }}>
        <div className="relative overflow-hidden rounded group w-full h-full">
          <img src={image2Url} alt="Image 2" className={`w-full h-auto object-cover transition-opacity duration-500 rounded ${imageStretch ? 'object-contain' : 'object-cover'}`} />
          {image2HoverUrl && (
            <img 
              src={image2HoverUrl} 
              alt="Image 2 Hover" 
              className="absolute inset-0 w-full h-auto object-cover opacity-0 group-hover:opacity-100 transition-all duration-700 delay-200 transform translate-y-[-40%] group-hover:translate-y-0 rounded" 
            />
          )}
        </div>
      </div>
    );
  };

  const renderText = () => (
    <div className="p-4 grow-0 flex items-center justify-center w-full lg:w-auto" style={{ flex: `0 0 ${textWidth}` }}>
      <div>{textContent}</div>
    </div>
  );

  const getOrderedContentMobile = () => {
    if (!image2Url) {
      // If no image2, only show image1 and text
      return order.startsWith('text')
        ? [renderText(), renderImage1()].map((element, index) => React.cloneElement(element, { key: `mobile-text-image1-${index}` }))
        : [renderImage1(), renderText()].map((element, index) => React.cloneElement(element, { key: `mobile-image1-text-${index}` }));
    }
    
    // For mobile with two images, always stack them vertically
    return [
      <div key="images" className="flex flex-col md:flex-row w-full lg:hidden">
        {renderImage1()}
        {renderImage2()}
      </div>,
      renderText()
    ];
  };

  const getOrderedContentDesktop = () => {
    if (!image2Url) {
      // If no image2, only show image1 and text
      return order.startsWith('text')
        ? [renderText(), renderImage1Desktop()].map((element, index) => React.cloneElement(element, { key: `desktop-text-image1-${index}` }))
        : [renderImage1Desktop(), renderText()].map((element, index) => React.cloneElement(element, { key: `desktop-image1-text-${index}` }));
    }
    
    switch (order) {
      case 'image-text-image':
        return [renderImage1Desktop(), renderText(), renderImage2Desktop()].map((element, index) => React.cloneElement(element, { key: `desktop-image1-text-image2-${index}` }));
      case 'text-image-image':
        return [renderText(), renderImage1Desktop(), renderImage2Desktop()].map((element, index) => React.cloneElement(element, { key: `desktop-text-image1-image2-${index}` }));
      case 'image-image-text':
      default:
        return [renderImage1Desktop(), renderImage2Desktop(), renderText()].map((element, index) => React.cloneElement(element, { key: `desktop-image1-image2-text-${index}` }));
    }
  };

  return (
    <Card
      ref={ref}
      className={cn(
        "rounded-sm w-full border px-3 sm:px-6 md:px-8 lg:px-12 xl:px-16 py-3 sm:py-6 md:py-8 lg:py-12 xl:py-16 gap-4 items-center justify-between",
        "flex flex-col lg:flex-row", // Stack vertically on mobile, horizontally on desktop
        bgColorFade,
        borderColor,
        className,
      )}
      {...props}
    >
     {/* Mobile layout */}
     <div className="flex flex-col w-full lg:hidden gap-4">
       {getOrderedContentMobile()}
     </div>
     
     {/* Desktop layout */}
     <div className="hidden lg:flex w-full gap-4 items-center justify-between">
       {getOrderedContentDesktop()}
     </div>
    </Card>
  );
});

WideGalleryCard.displayName = "WideGalleryCard";

export { GalleryCard, WideGalleryCard };
